// Permissions matrix editor (iter268). Admin picks a role and sets
// every tab / sub-tab to no access, read-only or read & write. Saved to the
// `role_permissions` document and enforced by the backend middleware.
import { useEffect, useState } from "react";
import { ShieldCheck, RotateCcw, Save, Loader2 } from "lucide-react";
import { btn } from "@/components/common/btn";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getAccessMatrix, setRoleAccess, resetRoleAccess } from "@/lib/api";
import { SettingsPanel, PanelHeader, PanelToolbar, Callout, LoadingState, EmptyState } from "@/components/settings/SettingsUI";
import { useAuth } from "@/auth/AuthContext";

const ROLE_LABEL = {
  admin: "Admin", service_manager: "Service Manager", project_manager: "Project Manager",
  account_manager: "Account Manager", accounting: "Accounting", office: "Accounting (legacy)", lead_technician: "Lead Technician",
  technician: "Technician", warehouse: "Warehouse", viewer: "Viewer",
};

export default function RolePermissionsPanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [role, setRole] = useState("service_manager");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const load = () => getAccessMatrix()
    .then(r => { setData(r.data); setDraft(r.data.roles[role]?.surfaces || {}); })
    .catch(() => setError(true));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (data) setDraft(data.roles[role]?.surfaces || {}); }, [role, data]);

  if (error) return (
    <SettingsPanel>
      <EmptyState icon={ShieldCheck} title="No access to permissions" hint="Ask an admin to grant you the Settings · Users area." testId="role-perms-forbidden" />
    </SettingsPanel>
  );
  if (!data) return <SettingsPanel><LoadingState testId="role-perms-loading" /></SettingsPanel>;

  const canEdit = data.can_edit !== false;

  const save = async () => {
    setSaving(true);
    try {
      await setRoleAccess(role, draft);
      await load();
      toast.success(`${ROLE_LABEL[role] || role} permissions saved`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      const r = await resetRoleAccess(role);
      setDraft(r.data.surfaces);
      await load();
      toast.success("Reset to defaults");
    } catch { toast.error("Reset failed"); }
  };

  const isAdmin = role === "admin";
  const locked = isAdmin || !canEdit;
  const adminsToAsk = (data.admin_emails || []).filter(e => e !== (user?.email || "").toLowerCase());
  const byGroup = data.groups.map(g => ({ group: g, items: data.surfaces.filter(s => s.group === g) }));

  return (
    <SettingsPanel testId="role-permissions-panel">
      <PanelHeader
        icon={ShieldCheck}
        title="Permissions"
        hint="Which tabs and sub-tabs each role can see and use"
        count={data.roles[role]?.customized ? "customized" : "defaults"}
        actions={
          canEdit ? (
            <>
              <Button size="sm" variant="outline" onClick={reset} disabled={isAdmin} data-testid="btn-reset-role-perms">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset
              </Button>
              <button type="button" className={`${btn("write")} h-8 w-8 justify-center !px-0`} onClick={save} disabled={saving || isAdmin}
                title={saving ? "Saving…" : "Save"} aria-label="Save" data-testid="btn-save-role-perms">
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              </button>
            </>
          ) : (
            <span className="text-[11px] uppercase tracking-wide text-slate-500 px-2 py-1 rounded bg-slate-100" data-testid="role-perms-readonly-badge">
              Read only
            </span>
          )
        }
      />
      <PanelToolbar>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Role</span>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-9 w-56 text-sm" data-testid="select-perm-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.keys(data.roles).map(r => (
                <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PanelToolbar>

      <div className="px-5 py-4 space-y-4">
        {isAdmin ? (
          <Callout tone="warn">
            Admin always has full control and cannot be edited. Admin-only areas (Users, Data
            Management, Go-Live Cleanup) are additionally locked to {data.admin_emails.join(" and ")}.
          </Callout>
        ) : !canEdit ? (
          <Callout tone="warn">
            You can see every role's access but only an admin can change it.
            {adminsToAsk.length > 0 && ` Ask ${adminsToAsk.join(" or ")} if something needs to move.`}
          </Callout>
        ) : (
          <Callout>Saved changes are enforced server-side right away; signed-in users see the new tabs on their next page refresh. JumpCloud only controls who can sign in — these permissions control what they see.</Callout>
        )}

        {byGroup.map(({ group, items }) => (
          <div key={group} className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50/60 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
              {group}
            </div>
            <div className="divide-y">
              {items.map(s => (
                <div key={s.id} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`perm-row-${s.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-800 truncate">{s.label}</div>
                    <div className="text-[11px] text-slate-400 font-mono truncate">{s.id}</div>
                  </div>
                  <Select
                    value={draft[s.id] || "none"}
                    onValueChange={(v) => setDraft(prev => ({ ...prev, [s.id]: v }))}
                    disabled={locked}
                  >
                    <SelectTrigger className="h-8 w-36 text-xs shrink-0" data-testid={`select-perm-${s.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Access</SelectItem>
                      <SelectItem value="read">Read Only</SelectItem>
                      <SelectItem value="write">Read &amp; write</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SettingsPanel>
  );
}
